import { APP_INITIALIZER, inject, Injectable, Injector, Provider } from "@angular/core";
import { Router } from "@angular/router";
import { NativePurchases } from "@capgo/native-purchases";

import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { AppleConfigService } from "@bitwarden/web-vault/app/services/api/apple-config.service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";

/**
 * Native side of the iOS points-to-offer-code redemption (#5928). The user finishes it in the App
 * Store, often after leaving Credits, so it can't live in PointsRedemptionComponent: that is
 * rebuilt on every Credits visit and Cash Back tab switch, and each instance used to add its own
 * listeners and never remove them. Root-provided, so its listener is registered once; and
 * created at startup (offerCodeRedemptionInitializer), not on the first Credits visit: the native
 * plugin hands each StoreKit transaction to whichever listener exists when it arrives and drops it
 * otherwise, and iOS often restarts the app while the user is in the App Store (#6045).
 */
@Injectable({ providedIn: "root" })
export class OfferCodeRedemptionService {
  // Fetched when a transaction arrives, not at startup: AppleConfigService configures RevenueCat
  // as it's created, which a Credits or plans visit did before.
  private injector = inject(Injector);
  private notifyService = inject(ConfirmationDialogService);
  private router = inject(Router);

  constructor() {
    void NativePurchases.addListener("transactionUpdated", (transaction: any) =>
      this.onTransactionUpdated(transaction),
    );
  }

  // A redemption is applied here, and the app reloads to show it. Nothing on resume: the app can't
  // tell from there whether the user redeemed anything (#6046).
  private async onTransactionUpdated(transaction: any): Promise<void> {
    try {
      const response = await this.injector
        .get(AppleConfigService)
        .verifyOfferCodeTransaction(transaction.jwsRepresentation);
      // Every StoreKit transaction comes here, renewals at each launch too: the server credits only
      // an offer-code redemption, once (gloss-api#389), and nothing changed for the rest.
      if (response?.result === "ignored" || response?.result === "duplicate") {
        return;
      }
      await this.router.navigate(["/"]);
      window.location.reload();
    } catch (e) {
      this.notifyService.notify(NotificationEnum.error, "error", "failedToRedeem");
    }
  }
}

/** Creates OfferCodeRedemptionService, and so its listener, when the app starts (#6045). */
export const offerCodeRedemptionInitializer: Provider = {
  provide: APP_INITIALIZER,
  multi: true,
  useFactory: () => {
    inject(OfferCodeRedemptionService);
    return (): void => undefined;
  },
};
